import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  size?: ModalSize;
  children: ReactNode;
  footer?: ReactNode;
};

const sizeClass: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-7xl',
};

export function Modal({ isOpen, onClose, title, subtitle, size = 'md', children, footer }: Props) {
  if (!isOpen) return null;

  const isXlSize = size === 'xl';

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm'
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'bg-card border border-border/50 rounded-2xl shadow-2xl w-full mx-4 p-6',
          sizeClass[size],
          isXlSize && 'max-h-[80vh] flex flex-col'
        )}
      >
        <div className={cn('flex items-center justify-between mb-5', isXlSize && 'shrink-0')}>
          <div>
            <h3 className='text-base font-semibold tracking-wider text-foreground'>{title}</h3>
            {subtitle && (
              <p className='text-sm text-muted-foreground mt-0.5 truncate max-w-70'>{subtitle}</p>
            )}
          </div>
          <Button
            variant='ghost'
            size='icon'
            onClick={onClose}
            className='size-8 rounded-lg text-muted-foreground hover:bg-muted/60 hover:text-foreground'
          >
            <X className='w-4 h-4' />
          </Button>
        </div>

        {isXlSize ? <div className='overflow-y-auto flex-1 min-h-0'>{children}</div> : children}

        {footer && (
          <div className={cn('flex gap-3', isXlSize ? 'shrink-0 mt-5' : 'mt-6')}>{footer}</div>
        )}
      </motion.div>
    </div>
  );
}
